import { useState, useCallback, useMemo } from "react";

export const PAGE_SIZE = 25;

export interface PaginationState {
  page: number;
  pageSize: number;
  from: number;
  to: number;
  totalCount: number;
  totalPages: number;
  setPage: (p: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  setTotalCount: (n: number) => void;
  setPageSize: (size: number) => void;
  canNext: boolean;
  canPrev: boolean;
}

export function usePagination(initialPageSize = PAGE_SIZE): PaginationState {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const nextPage = useCallback(() => setPage((p) => Math.min(p + 1, totalPages)), [totalPages]);
  const prevPage = useCallback(() => setPage((p) => Math.max(p - 1, 1)), []);
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPage(1);
  }, []);

  return useMemo(() => ({
    page,
    pageSize,
    from,
    to,
    totalCount,
    totalPages,
    setPage,
    nextPage,
    prevPage,
    setTotalCount,
    setPageSize,
    canNext: page < totalPages,
    canPrev: page > 1,
  }), [page, pageSize, from, to, totalCount, totalPages, nextPage, prevPage, setPageSize]);
}
